/**
 * `orders/{orderId}` — sección 7b.
 *
 * El documento es el ESTADO; la lógica de transiciones vive en ../orders/stateMachine.ts
 * (puro, sin Firebase) y Functions solo la ejecuta y persiste. Solo Functions escribe aquí.
 *
 * Decisiones aprobadas el 13/10/2026:
 *   - `pending_payment` al principio (ventana entre crear el PaymentIntent y la autorización).
 *   - `fulfillmentMode: 'shipping' | 'handover'` (C7, "en mano" con QR) como atributo del
 *     pedido, no como estados aparte.
 */
import { z } from 'zod';
import {
  ActorSchema,
  BundleIdSchema,
  CarrierIdSchema,
  CurrencySchema,
  DisputeIdSchema,
  MoneySchema,
  OfferIdSchema,
  OrderIdSchema,
  PaymentIdSchema,
  ProductIdSchema,
  ShipmentIdSchema,
  TimestampSchema,
  UserIdSchema,
} from './primitives.js';

export const ORDER_STATUSES = [
  'pending_payment',
  'paid_pending_acceptance',
  'accepted',
  'label_ready',
  'shipped',
  'in_transit',
  'delivered',
  'completed',
  'disputed',
  'cancelled_by_buyer',
  'cancelled_by_seller',
  'expired_acceptance',
  'expired_shipping',
  'refunded',
] as const;
export const OrderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const TERMINAL_ORDER_STATUSES = [
  'completed',
  'cancelled_by_buyer',
  'cancelled_by_seller',
  'expired_acceptance',
  'expired_shipping',
  'refunded',
] as const satisfies readonly OrderStatus[];
export type TerminalOrderStatus = (typeof TERMINAL_ORDER_STATUSES)[number];

export const FULFILLMENT_MODES = ['shipping', 'handover'] as const;
export const FulfillmentModeSchema = z.enum(FULFILLMENT_MODES);
export type FulfillmentMode = z.infer<typeof FulfillmentModeSchema>;

/** Cómo nació el pedido: compra directa, oferta/contraoferta aceptada o lote. */
export const ORDER_ORIGINS = ['buy_now', 'offer', 'bundle'] as const;
export const OrderOriginSchema = z.enum(ORDER_ORIGINS);
export type OrderOrigin = z.infer<typeof OrderOriginSchema>;

/** Desglose en la moneda del COMPRADOR (lo que se cobró). */
export const OrderAmountsSchema = z
  .object({
    item: MoneySchema,
    protection_fee: MoneySchema,
    shipping: MoneySchema,
    surcharge: MoneySchema,
    total: MoneySchema,
  })
  .refine(
    (a) =>
      [a.item, a.protection_fee, a.shipping, a.surcharge].every(
        (m) => m.currency === a.total.currency,
      ) &&
      a.item.amount + a.protection_fee.amount + a.shipping.amount + a.surcharge.amount ===
        a.total.amount,
    { message: 'amounts: la suma de las partes debe ser igual al total y en la misma moneda' },
  );
export type OrderAmounts = z.infer<typeof OrderAmountsSchema>;

/** Precio del artículo en la moneda del VENDEDOR (lo que se le transferirá) + tipo aplicado. */
export const OrderSellerAmountSchema = z.object({
  item: MoneySchema,
  fx_rate: z.number().positive(),
  fx_source: z.string().max(40),
});

export const OrderDeadlinesSchema = z.object({
  /** 48 h desde el pago (paid_pending_acceptance). */
  accept_by: TimestampSchema.nullable(),
  /** 7 días desde que la etiqueta está lista (shipping) o desde la aceptación (handover). */
  ship_by: TimestampSchema.nullable(),
  /** 48 h desde la entrega: ventana de reclamación. */
  dispute_by: TimestampSchema.nullable(),
  /** Momento en que se libera el pago si no hay disputa (= dispute_by). */
  release_at: TimestampSchema.nullable(),
  /** Nombres de las Cloud Tasks programadas, por vencimiento, para poder cancelarlas. */
  task_ids: z.record(
    z.enum(['reserved_until', 'accept_by', 'ship_by', 'release_at', 'dispute_response']),
    z.string(),
  ),
});
export type OrderDeadlines = z.infer<typeof OrderDeadlinesSchema>;

export const OrderHistoryEntrySchema = z.object({
  at: TimestampSchema,
  from: OrderStatusSchema.nullable(),
  to: OrderStatusSchema,
  event: z.string().min(1).max(60),
  actor: ActorSchema,
  reason: z.string().max(500).optional(),
  /** `${orderId}:${event}:${from}` — una transición no se aplica dos veces. */
  idempotency_key: z.string().min(1),
});
export type OrderHistoryEntry = z.infer<typeof OrderHistoryEntrySchema>;

export const OrderItemSchema = z.object({
  product_id: ProductIdSchema,
  /** Instantánea para que el pedido se lea aunque el anuncio cambie o se borre. */
  title: z.string(),
  photo_url: z.string().url().nullable(),
  /** Precio del artículo en moneda del vendedor, céntimos. */
  price: MoneySchema,
  quantity: z.number().int().min(1),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

/** Opción de envío elegida por el comprador (sección 8): transportista + servicio (+ punto). */
export const ShippingChoiceSchema = z.object({
  carrier_id: CarrierIdSchema,
  service_code: z.string().min(1).max(80),
  service_level: z.enum(['locker', 'pickup_point', 'home']),
  name: z.string().max(120),
  carrier_name: z.string().max(80),
  pickup_point: z
    .object({ id: z.string().max(80), name: z.string().max(200), address: z.string().max(300) })
    .nullable(),
  /** Lo que paga el comprador por el envío (su moneda) y el coste del transportista (moneda del carrier). */
  price: MoneySchema,
  cost: MoneySchema,
  meta: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type ShippingChoice = z.infer<typeof ShippingChoiceSchema>;

export const OrderSchema = z.object({
  id: OrderIdSchema,
  status: OrderStatusSchema,
  fulfillment_mode: FulfillmentModeSchema,
  origin: OrderOriginSchema,
  buyer_id: UserIdSchema,
  seller_id: UserIdSchema,
  items: z.array(OrderItemSchema).min(1),
  offer_id: OfferIdSchema.nullable(),
  bundle_id: BundleIdSchema.nullable(),
  /** Solo pedidos de lote (D5): suma de artículos y descuento del vendedor, en moneda del vendedor. */
  bundle_subtotal: MoneySchema.optional(),
  bundle_discount_percent: z.number().int().min(0).max(100).optional(),
  bundle_discount: MoneySchema.optional(),
  /** Reembolso parcial de una incidencia (moneda del comprador) y su parte en la moneda del vendedor. */
  refund_amount: MoneySchema.optional(),
  refund_seller_amount: MoneySchema.optional(),
  /** A3 (T16): este pedido usó la primera compra sin tarifa del invitado. */
  referral_fee_waived: z.boolean().optional(),
  /** L4: el comprador añadió la protección opcional (artículo por encima del umbral). */
  optional_protection: z.boolean().optional(),
  /** B5: campaña de envío aplicada y lo que asumió nuvvu (moneda del comprador). */
  shipping_campaign_id: z.string().optional(),
  campaign_subsidy: MoneySchema.optional(),
  /** Desistimiento (14 días, compras a profesional): comunicación enviada. */
  withdrawal_notice_id: z.string().optional(),
  withdrawal_notice_at: TimestampSchema.optional(),
  shipping_option: ShippingChoiceSchema.optional(),
  /** Cuándo se completó (para la valoración automática a los 4 días) y quién ha valorado ya. */
  completed_at: TimestampSchema.optional(),
  reviewed_by_buyer: z.boolean().optional(),
  reviewed_by_seller: z.boolean().optional(),
  amounts: OrderAmountsSchema,
  seller_amount: OrderSellerAmountSchema,
  buyer_currency: CurrencySchema,
  /**
   * Dirección/punto de entrega congelados en el pedido (no referencia a users/addresses).
   * 27/10 (privacidad): en los pedidos nuevos aquí solo van CP, ciudad y país; el nombre y la
   * dirección completos viven en `orders/{id}/private/delivery`, que el vendedor solo puede leer
   * con `address_visible_to_seller` (envío manual: sin transportista o la etiqueta falló).
   */
  delivery: z
    .discriminatedUnion('kind', [
      z.object({
        kind: z.literal('address'),
        address: z.object({
          full_name: z.string().optional(),
          line1: z.string().optional(),
          line2: z.string().optional(),
          postal_code: z.string(),
          city: z.string(),
          region: z.string().optional(),
          country_code: z.string().regex(/^[A-Z]{2}$/),
          phone: z.string().optional(),
        }),
      }),
      z.object({
        kind: z.literal('pickup_point'),
        carrier_id: z.string(),
        point_id: z.string(),
        point_name: z.string(),
        country_code: z.string().regex(/^[A-Z]{2}$/),
      }),
      z.object({ kind: z.literal('handover'), note: z.string().max(500).optional() }),
    ])
    .nullable(),
  carrier_id: z.string().nullable(),
  /** El vendedor puede ver nombre y dirección del comprador (envío manual). Ver `delivery`. */
  address_visible_to_seller: z.boolean().optional(),
  payment_id: PaymentIdSchema,
  shipment_id: ShipmentIdSchema.nullable(),
  dispute_id: DisputeIdSchema.nullable(),
  /** C7: hash del QR de entrega en mano; el escaneo lleva de accepted a completed. */
  handover_code_hash: z.string().nullable(),
  deadlines: OrderDeadlinesSchema,
  history: z.array(OrderHistoryEntrySchema),
  cancel_reason: z.string().max(500).nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  /** Marca del script de migración (sección 17). */
  migrated_from_v1: z
    .object({ legacy_status: z.string(), migrated_at: TimestampSchema })
    .optional(),
});
export type Order = z.infer<typeof OrderSchema>;

export const ordersPath = () => 'orders' as const;
export const orderPath = (orderId: string) => `orders/${orderId}` as const;

// ---------- offers / bundles (igual que hoy + expires_at obligatorio) ----------

export const OFFER_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'countered',
  'expired',
  'withdrawn',
] as const;
export const OfferSchema = z.object({
  id: OfferIdSchema,
  product_id: ProductIdSchema,
  buyer_id: UserIdSchema,
  seller_id: UserIdSchema,
  amount: MoneySchema,
  status: z.enum(OFFER_STATUSES),
  /** Obligatorio (sección 7): 24 h. */
  expires_at: TimestampSchema,
  counter_of: OfferIdSchema.nullable(),
  order_id: OrderIdSchema.nullable(),
  /** H7 (27/10): aceptada → 24 h para pagar; se borra al usarla. */
  pay_by: TimestampSchema.nullable().optional(),
  /** H7: pagada (un solo uso). */
  used_at: TimestampSchema.nullable().optional(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Offer = z.infer<typeof OfferSchema>;

export const BundleSchema = z.object({
  id: BundleIdSchema,
  buyer_id: UserIdSchema,
  seller_id: UserIdSchema,
  product_ids: z.array(ProductIdSchema).min(2),
  /** Descuento por tramos 2/3/5 (D5), en porcentaje entero. */
  discount_percent: z.number().int().min(0).max(100),
  status: z.enum(OFFER_STATUSES),
  expires_at: TimestampSchema,
  order_id: OrderIdSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Bundle = z.infer<typeof BundleSchema>;

// ---------- disputes ----------

export const DISPUTE_REASONS = [
  'not_received',
  'not_as_described',
  'damaged',
  'counterfeit',
  'wrong_item',
  'other',
] as const;
export const DISPUTE_STATUSES = [
  'open',
  'awaiting_seller',
  /** El vendedor propuso un parcial que su venta no cubre y está pagando la diferencia con tarjeta. */
  'awaiting_seller_payment',
  'awaiting_buyer',
  'in_mediation',
  /** Devolución física en marcha: el comprador reenvía el artículo y el vendedor confirma (7b). */
  'awaiting_return',
  'resolved_refund_full',
  'resolved_refund_partial',
  'resolved_return',
  'resolved_release',
  'closed',
] as const;

export const DisputeSchema = z.object({
  id: DisputeIdSchema,
  order_id: OrderIdSchema,
  buyer_id: UserIdSchema,
  seller_id: UserIdSchema,
  reason: z.enum(DISPUTE_REASONS),
  description: z.string().max(4000),
  /** Fotos obligatorias (sección 10, #4). */
  photo_urls: z.array(z.string().url()).min(1).max(10),
  status: z.enum(DISPUTE_STATUSES),
  /** 72 h para que responda el vendedor; 7 días para resolver (7b). */
  seller_respond_by: TimestampSchema,
  resolve_by: TimestampSchema,
  /** Respuesta del vendedor en sus 72 h (7b): devolver todo, proponer parcial o rechazar (mediación). */
  seller_response: z
    .object({
      kind: z.enum(['refund_full', 'refund_partial', 'reject', 'return']),
      /** Parcial: lo que recibe el comprador (artículo + envío si se incluye). */
      refund_amount: MoneySchema.nullable(),
      /** Parcial (20/10): parte del artículo (≤ su precio) y si incluye los gastos de envío enteros. */
      item_refund: MoneySchema.nullable().optional(),
      include_shipping: z.boolean().optional(),
      note: z.string().max(2000).optional(),
      at: TimestampSchema,
    })
    .nullable()
    .optional(),
  /**
   * Parcial que la venta no cubre (20/10): el vendedor pone la diferencia al proponer, saldo del
   * wallet primero (retenido hasta que el comprador decide) y el resto con tarjeta (Stripe Checkout).
   * Si el comprador rechaza o caduca, se le devuelve todo. `attempt` separa intentos sucesivos.
   */
  seller_cover: z
    .object({
      attempt: z.number().int().min(1),
      shortfall: MoneySchema,
      wallet_portion: MoneySchema,
      card_portion: MoneySchema,
      checkout_session_id: z.string().nullable(),
      checkout_url: z.string().nullable(),
      payment_intent_id: z.string().nullable(),
      paid_at: TimestampSchema.nullable(),
      captured_at: TimestampSchema.nullable(),
      released_at: TimestampSchema.nullable(),
    })
    .nullable()
    .optional(),
  /**
   * Devolución física (7b): el comprador tiene `ship_by` para reenviar el artículo (a su cargo, sin
   * etiqueta de nuvvu) y el vendedor `confirm_by` desde el envío para confirmar que lo recibió; al
   * confirmar (o si no responde) se reembolsa todo; si el comprador no lo envía, se libera el pago.
   */
  return: z
    .object({
      proposed_at: TimestampSchema,
      ship_by: TimestampSchema,
      shipped_at: TimestampSchema.nullable(),
      confirm_by: TimestampSchema.nullable(),
      tracking_number: z.string().max(80).nullable(),
      received_at: TimestampSchema.nullable(),
    })
    .nullable()
    .optional(),
  /** Efecto de dinero que falló y el barrido reintenta (auditoría 27/10, A1). */
  money_pending: z
    .object({
      action: z.enum([
        'capture',
        'release_authorization',
        'refund_full',
        'refund_partial',
        'transfer_to_seller',
      ]),
      attempts: z.number().int().min(1),
      last_error: z.string().max(300),
      since: TimestampSchema,
      updated_at: TimestampSchema,
    })
    .nullable()
    .optional(),
  /** Respuesta del comprador a la propuesta parcial. */
  buyer_response: z.object({ accepted: z.boolean(), at: TimestampSchema }).nullable().optional(),
  resolution: z
    .object({
      kind: z.enum(['refund_full', 'refund_partial', 'return', 'release']),
      refund_amount: MoneySchema.nullable(),
      decided_by: ActorSchema,
      note: z.string().max(2000).optional(),
      decided_at: TimestampSchema,
    })
    .nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Dispute = z.infer<typeof DisputeSchema>;
export const disputePath = (id: string) => `disputes/${id}` as const;
